import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  AccountTopupConfirmation,
  InvestmentPackageSettings,
  Money as MoneyModel,
} from 'api-client';
import protection from 'assets/images/svg/protection-black.svg';
import { DocumentCard } from 'components/common/documents';
import { CheckIcon, ChevronIcon, CrossIcon } from 'components/common/icons';
import { InputMoney } from 'components/common/inputs';
import { Toggle } from 'components/common/selects';
import { ConfirmSmsCodeForm, Loader, Money } from 'components/common/utils';
import {
  createAccountTopupConfirmationManager,
  createInvestmentPackageManager,
  createProfileManager,
} from 'di';
import { useAppDispatch, useErrorCallback, useProfileData } from 'hooks';
import { set as setProfile } from 'store/profile';
import { cn, formatAmount } from 'utils';

import { PrimaryButton } from '../../buttons';
import { PrimaryButtonColor } from '../../buttons/decorators/PrimaryButton/PrimaryButton';
import { Image } from '../../images';
import { formatAmountRub, getDealsPerPackage, getMinTopupKopecks } from '../comfortAccountSettings';
import styles from '../ComfortAccountWizardModal/ComfortAccountWizardModal.module.scss';
import Modal from '../Modal';
import { ModalProps } from '../Modal/Modal';
import topupStyles from './ComfortAccountTopupModal.module.scss';

type TopupStep = 'amount' | 'confirm' | 'sms' | 'result';

type ResultState =
  | { kind: 'success' }
  | {
      kind: 'error';
      message: string;
    };

type Props = ModalProps & {
  availableOnMainAccount: MoneyModel;
  onSuccess?: () => void;
};

const ComfortAccountTopupModal: FC<Props> = ({
  active,
  onClose,
  availableOnMainAccount,
  onSuccess,
}) => {
  const handleError = useErrorCallback();
  const profileData = useProfileData();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<TopupStep>('amount');
  const [settings, setSettings] = useState<InvestmentPackageSettings | null>(null);
  const [amount, setAmount] = useState<MoneyModel>(
    () => new MoneyModel(0, availableOnMainAccount.currencyCode)
  );
  const [capitalProtection, setCapitalProtection] = useState(false);
  const [confirmation, setConfirmation] = useState<AccountTopupConfirmation | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleErrorRef = useRef(handleError);
  const settingsLoadedRef = useRef(false);
  const isSubmittingRef = useRef(false);

  const minTopupKopecks = settings ? getMinTopupKopecks(settings) : 0;
  const dealsPerPackage = settings ? getDealsPerPackage(settings) : 10;
  const amountRub = useMemo(() => Math.floor(amount.amount / 100), [amount.amount]);
  const minTopupRub = Math.floor(minTopupKopecks / 100);

  const commissionAdvance = useMemo(() => {
    const feeSettings = profileData.investmentFee;
    if (feeSettings?.percent) {
      return new MoneyModel((amount.amount * feeSettings.percent) / 100, amount.currencyCode);
    }
    if (feeSettings?.money) {
      return feeSettings.money;
    }
    return new MoneyModel(0, amount.currencyCode);
  }, [amount, profileData.investmentFee]);

  const totalAmount = useMemo(
    () => new MoneyModel(amount.amount + commissionAdvance.amount, amount.currencyCode),
    [amount.amount, amount.currencyCode, commissionAdvance]
  );

  const amountIsValid = amount.amount >= minTopupKopecks && amount.amount > 0;
  const hasSufficientBalance = totalAmount.amount <= availableOnMainAccount.amount;
  const canSubmitAmount = amountIsValid && hasSufficientBalance;
  const canConfirm = Boolean(confirmation);

  const getMinAmountError = () => {
    if (settings) {
      return `Минимальная сумма: ${formatAmountRub(settings.minAmount)}`;
    }
    return `Минимальная сумма: ${formatAmount(minTopupRub)} ₽`;
  };

  const setInlineError = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) {
      setStepError(error.message);
      return;
    }
    setStepError(fallback);
  };

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    if (!active) {
      settingsLoadedRef.current = false;
      isSubmittingRef.current = false;
      return;
    }

    if (settingsLoadedRef.current) {
      return;
    }

    settingsLoadedRef.current = true;
    setStep('amount');
    setAmount(
      new MoneyModel(
        settings ? getMinTopupKopecks(settings) : 0,
        availableOnMainAccount.currencyCode
      )
    );
    setCapitalProtection(false);
    setConfirmation(null);
    setResult(null);
    setStepError(null);
    setIsSubmitting(false);

    const loadSettings = async () => {
      try {
        const manager = await createInvestmentPackageManager();
        const loadedSettings = await manager.getSettings();
        setSettings(loadedSettings);
        setAmount(
          new MoneyModel(getMinTopupKopecks(loadedSettings), availableOnMainAccount.currencyCode)
        );
      } catch (error) {
        if (error instanceof Error) {
          await handleErrorRef.current(error);
        }
      }
    };

    void loadSettings();
  }, [active, availableOnMainAccount.currencyCode, settings]);

  const close = () => onClose();

  const headerTitle = 'Настройка пакета сделок';
  const showBack = step === 'confirm' || step === 'sms' || step === 'result';
  const showClose = !isSubmitting;

  const goBack = () => {
    setStepError(null);
    if (step === 'confirm') setStep('amount');
    else if (step === 'sms') setStep('confirm');
    else if (step === 'result') close();
  };

  const handleAmountConfirm = () => {
    setStepError(null);

    if (!amountIsValid) {
      if (amount.amount > 0) {
        setStepError(getMinAmountError());
      }
      return;
    }

    if (!hasSufficientBalance) {
      setStepError('Недостаточно средств на основном счете');
      return;
    }

    void createConfirmation();
  };

  const createConfirmation = async () => {
    if (!canSubmitAmount || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setStepError(null);
    setIsSubmitting(true);

    try {
      const manager = await createAccountTopupConfirmationManager();
      const { uuid } = await manager.create(amount, capitalProtection);
      const confirmationData = await manager.get(uuid);
      setConfirmation(confirmationData);
      setStep('confirm');
    } catch (error) {
      setInlineError(error, 'К сожалению, произошла ошибка. Попробуйте снова');
      setStep('amount');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const sendSmsAndGoToCode = async () => {
    if (!confirmation || !canConfirm || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setStepError(null);
    setIsSubmitting(true);

    try {
      const manager = await createAccountTopupConfirmationManager();
      await manager.sendCode(confirmation.confirmationUuid);
      setStep('sms');
    } catch (error) {
      setInlineError(error, 'К сожалению, произошла ошибка. Попробуйте снова');
      setStep('confirm');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleConfirmSubmit = () => void sendSmsAndGoToCode();

  const handleAmountChanged = (money: MoneyModel) => {
    setAmount(money);
    setStepError(null);
  };

  const handleCapitalProtectionToggle = () => setCapitalProtection((value) => !value);

  const handleSmsCancel = () => setStep('confirm');

  const sendSmsCode = async (smsCode: string) => {
    if (!confirmation) {
      throw new Error('Отсутствует подтверждение пополнения');
    }
    const manager = await createAccountTopupConfirmationManager();
    return manager.confirmCode(smsCode, confirmation.confirmationUuid);
  };

  const resendSmsCode = async () => {
    if (!confirmation) return;
    const manager = await createAccountTopupConfirmationManager();
    await manager.sendCode(confirmation.confirmationUuid);
  };

  const handleTopupSuccess = async () => {
    try {
      const manager = await createProfileManager();
      const profile = await manager.get();
      dispatch(setProfile(profile));
    } catch (error) {
      if (error instanceof Error) {
        await handleErrorRef.current(error);
      }
    }
    setResult({ kind: 'success' });
    onSuccess?.();
    setStep('result');
  };

  const handleTopupError = async (error: Error) => {
    setResult({
      kind: 'error',
      message: error.message || 'К сожалению, произошла ошибка. Попробуйте снова',
    });
    setStep('result');
  };

  return (
    <Modal
      active={active}
      onClose={onClose}
      className={cn(styles.modal, step === 'sms' ? styles.modalSms : undefined)}
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {showBack ? (
            <button type="button" className={styles.backButton} onClick={goBack} aria-label="Назад">
              <ChevronIcon direction="left" color="var(--white-color)" />
            </button>
          ) : (
            <div className={styles.headerLeftSpacer} />
          )}
        </div>
        <div className={styles.headerTitle}>{headerTitle}</div>
        <div className={styles.headerRight}>
          {showClose ? (
            <button
              type="button"
              className={styles.closeButton}
              onClick={close}
              aria-label="Закрыть"
            >
              <CrossIcon preset="default" />
            </button>
          ) : (
            <div className={styles.headerRightSpacer} />
          )}
        </div>
      </div>

      <div className={styles.body}>
        <div className={topupStyles.stepShell}>
          <div className={topupStyles.stepMain}>
            {step === 'amount' ? (
              <div className={styles.amountStep}>
                {isSubmitting ? (
                  <div className={topupStyles.compactLoading}>
                    <Loader size="small" overlay={false} />
                  </div>
                ) : (
                  <>
                    <div className={styles.rowBetween}>
                      <div className={styles.mutedLabel}>Доступно на основном счете:</div>
                      <div className={styles.amountValue}>
                        <Money money={availableOnMainAccount} />
                      </div>
                    </div>

                    <div className={styles.sectionTitle}>
                      Введите сумму пополнения счета Комфорт для формирования пакета
                    </div>

                    <div className={styles.inputRow}>
                      <InputMoney
                        money={amount}
                        onValueChanged={handleAmountChanged}
                        placeholder="0,00"
                        aria-label="Сумма пополнения"
                        autoFocus
                      />
                    </div>

                    {!amountIsValid && amount.amount > 0 && !stepError ? (
                      <div className={styles.hintError}>{getMinAmountError()}</div>
                    ) : null}

                    <div className={styles.summaryRows}>
                      <div className={styles.summaryRow}>
                        <div className={styles.summaryLabel}>Аванс комиссии</div>
                        <div className={styles.summaryValue}>
                          <Money money={commissionAdvance} />
                        </div>
                      </div>
                      <div className={styles.summaryRow}>
                        <div className={styles.summaryLabel}>Итого</div>
                        <div className={styles.summaryValue}>
                          <Money money={totalAmount} />
                        </div>
                      </div>
                    </div>

                    <div className={styles.protectionRow}>
                      <div className={styles.protectionLeft}>
                        <Toggle
                          active={capitalProtection}
                          onToggled={handleCapitalProtectionToggle}
                          size="s"
                        />
                        <div className={styles.protectionText}>
                          Применить условие Защиты капитала
                        </div>
                        <Image src={protection} alt="protection" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {step === 'confirm' && confirmation ? (
              <div className={styles.confirmStep}>
                {isSubmitting ? (
                  <div className={topupStyles.compactLoading}>
                    <Loader size="small" overlay={false} />
                  </div>
                ) : (
                  <>
                    <div className={cn(styles.summaryRows, topupStyles.confirmSummaryRows)}>
                      <div className={styles.summaryRow}>
                        <div className={styles.summaryLabel}>Общая сумма зачисления</div>
                        <div className={styles.summaryValue}>{formatAmount(amountRub)} ₽</div>
                      </div>
                      <div className={styles.summaryRow}>
                        <div className={styles.summaryLabel}>Сумма в одну сделку</div>
                        <div className={styles.summaryValue}>
                          {formatAmount(Math.floor(amountRub / dealsPerPackage))} ₽
                        </div>
                      </div>
                    </div>

                    {confirmation.agreementLink ? (
                      <DocumentCard
                        title="Агентское поручение"
                        documentUrl={confirmation.agreementLink}
                      />
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            {step === 'sms' && confirmation ? (
              <ConfirmSmsCodeForm
                variant="comfort"
                compactLoading
                prefix="comfort-account-topup"
                title="Введите код"
                onCancel={handleSmsCancel}
                sendCallback={sendSmsCode}
                resendCallback={resendSmsCode}
                successCallback={handleTopupSuccess}
                onError={handleTopupError}
              />
            ) : null}

            {step === 'result' ? (
              <div className={styles.resultState}>
                {result?.kind === 'success' ? (
                  <div className={styles.resultRow}>
                    <CheckIcon preset="circle-success" size={24} />
                    <div className={styles.resultText}>Счет успешно пополнен</div>
                  </div>
                ) : (
                  <div className={styles.resultRow}>
                    <CrossIcon preset="circle-danger" size={24} />
                    <div className={styles.resultText}>
                      {result?.message ?? 'К сожалению, произошла ошибка. Попробуйте снова'}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={cn(
          styles.footer,
          step === 'sms' || step === 'result' ? styles.footerHidden : ''
        )}
      >
        {stepError ? <div className={styles.footerError}>{stepError}</div> : null}

        {step === 'amount' ? (
          <PrimaryButton
            color={
              canSubmitAmount && !isSubmitting ? PrimaryButtonColor.GREEN : PrimaryButtonColor.GRAY
            }
            expanded
            large
          >
            <button type="button" onClick={handleAmountConfirm} disabled={isSubmitting}>
              Подтвердить создание пакета
            </button>
          </PrimaryButton>
        ) : null}

        {step === 'confirm' ? (
          <PrimaryButton
            color={canConfirm && !isSubmitting ? PrimaryButtonColor.GREEN : PrimaryButtonColor.GRAY}
            expanded
            large
          >
            <button
              type="button"
              onClick={handleConfirmSubmit}
              disabled={!canConfirm || isSubmitting}
            >
              Подтвердить агентское поручение
            </button>
          </PrimaryButton>
        ) : null}
      </div>
    </Modal>
  );
};

export default ComfortAccountTopupModal;
